import { STORAGE_KEYS } from '../../config/storageKeys';
import { withAppBasename } from '../../config/appBase';

/**
 * The student join code from a /join#<code> link, kept in sessionStorage so
 * it survives sign-up, login and the Google/LinkedIn SSO round trip in the
 * same tab. Storage can be unavailable (private mode, blocked site data), so
 * every access is wrapped: a failed read means no pending code, a failed
 * write means the link has to be opened again after signing in.
 *
 * The code travels in the fragment, which browsers never send to a server.
 * public/index.html's join-link-guard moves it into storage and strips it
 * before any analytics tag runs; captureJoinCodeFromFragment does the same
 * from the page itself, for any load where that guard did not run.
 *
 * The code is a secret that admits someone to a school. Never log it, put it
 * in an analytics event or error report, or render it.
 */

export const JOIN_PATH = '/join';

export function storePendingJoinCode(code) {
  try {
    sessionStorage.setItem(STORAGE_KEYS.PENDING_JOIN_CODE, code);
  } catch (e) {
    // Storage unavailable; nothing to resume after sign-in.
  }
}

export function readPendingJoinCode() {
  try {
    return sessionStorage.getItem(STORAGE_KEYS.PENDING_JOIN_CODE);
  } catch (e) {
    return null;
  }
}

export function clearPendingJoinCode() {
  try {
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_JOIN_CODE);
  } catch (e) {
    // Storage unavailable; there is nothing stored to clear.
  }
}

/** Moves a code in the URL fragment into storage and strips the fragment
 * from the URL. Does nothing when there is no fragment. */
export function captureJoinCodeFromFragment() {
  const code = window.location.hash.replace(/^#/, '');
  if (!code) return;
  try {
    storePendingJoinCode(decodeURIComponent(code));
  } catch (e) {
    // A malformed fragment is not a code; drop it with the fragment.
  }
  // replaceState is a raw URL write, so it needs the /edu prefix the router adds elsewhere.
  window.history.replaceState(window.history.state, '', withAppBasename(JOIN_PATH));
}

/** Where to send a user who has just signed in: the join page if a join is
 * pending, otherwise `fallback`. */
export function postSignInPath(fallback) {
  return readPendingJoinCode() ? JOIN_PATH : fallback;
}
